import { Injectable, InjectionToken, inject } from '@angular/core';
import { DownloadFileService } from 'src/app/service/download-files-service/download-file.service';
import { AppPlatformService } from 'src/app/service/platform/app-platform.service';
import { UsuarioServiceProvider } from 'src/app/service/usuario-service/usuario-service';
import { ApiService } from 'src/app/api.service';
import { Observable, from, map } from 'rxjs';

import { TaskData, TaskDraft, TaskResource, TaskSubmitResponse } from './task-delivery.model';

/**
 * Where `app-task-delivery` gets and sends its data. The component owns the UI and the protocol
 * (validation, the `FormData` shape, the phases); the port owns the transport.
 *
 * The default is {@link ApiTaskDeliveryPort}, which talks to `ApiService` exactly as production does.
 * A host that must keep its writes inside its own data layer provides a different one on the
 * component's element — the learning path does this so its task goes through `LearningPathService`
 * (and therefore through the mock), instead of reaching the API directly.
 *
 * The three draft methods are optional: a host whose view of the component can be destroyed and
 * recreated while the learner is mid-delivery implements them, and the component then comes back
 * with its task and picked files instead of asking for them again. Without them (the default port)
 * the component keeps everything in its own fields, exactly as production's modal does.
 */
export interface TaskDeliveryPort {
  getTask(projectId: number): Observable<TaskData>;
  submitTask(projectId: number, formData: FormData): Observable<TaskSubmitResponse>;
  /** Downloads a reference resource. Read-only, so it is not part of any data source. */
  downloadResource(resource: TaskResource): Promise<void>;

  /** The draft kept for this task, or `null`. The component decides whether it is still valid. */
  restoreDraft?(projectId: number): TaskDraft | null;
  saveDraft?(projectId: number, draft: TaskDraft): void;
  clearDraft?(projectId: number): void;
}

export const TASK_DELIVERY_PORT = new InjectionToken<TaskDeliveryPort>('TASK_DELIVERY_PORT', {
  providedIn: 'root',
  factory: () => inject(ApiTaskDeliveryPort),
});

/**
 * Production's own calls, unchanged: `tareas/{id}/search`, `tareas/{id}/store-update` (the latter
 * still `fetch`, with its own token handling — see `ApiService.taskUpdate`) and the download branch
 * of `pages/tema`'s `descargarTarea()`.
 */
@Injectable({ providedIn: 'root' })
export class ApiTaskDeliveryPort implements TaskDeliveryPort {
  private readonly api = inject(ApiService);
  private readonly usuario = inject(UsuarioServiceProvider);
  private readonly downloadFile = inject(DownloadFileService);
  private readonly appPlatform = inject(AppPlatformService);

  getTask(projectId: number): Observable<TaskData> {
    return from(this.api.getTaskData(projectId)).pipe(
      map((response: { data: TaskData }) => response.data)
    );
  }

  submitTask(projectId: number, formData: FormData): Observable<TaskSubmitResponse> {
    return from(this.api.taskUpdate(projectId, formData));
  }

  async downloadResource(resource: TaskResource): Promise<void> {
    const file = resource.file ?? '';
    const fileName = file.split('/').pop() ?? '';

    if (this.appPlatform.isAppPlatform()) {
      const url = file.includes('https') ? file : this.usuario.getBucketBaseUrl + file;
      this.downloadFile.downloadMediaInMobileDevice(url, fileName);
      return;
    }

    const endpoint = `${this.api.getApiUrls.serviceBaseRest}tareas/resource/download?multimedia_id=${resource.resource_id}`;
    const token = localStorage.getItem('token');
    const response = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await response.blob();

    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = fileName || `${resource.title}.pdf`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }
}
